import { useEffect, useMemo, useRef, useState } from "react";
import { api, type Candidate } from "../lib/api";
import { useApp } from "../lib/store";

/** A command-palette-style picker over the verified-pro roster. Tap a
 *  player to add or remove them from your dashboard. Order stays stable
 *  while open (region first, then alpha) so toggling never reshuffles. */
export function AddPlayers({ open, onClose }: { open: boolean; onClose: () => void }) {
  const {
    candidates,
    isWatched,
    watchPlayer,
    unwatchPlayer,
    region,
    admin,
    merytz,
    addCandidate,
  } = useApp();
  const [q, setQ] = useState("");
  const [live, setLive] = useState<Candidate[] | null>(null);
  const [adding, setAdding] = useState(false);
  const trimmed = q.trim();

  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Esc to close + a focus trap, and restore focus to the opener on close.
  useEffect(() => {
    if (!open) return;
    openerRef.current = (document.activeElement as HTMLElement) ?? null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const f = panelRef.current.querySelectorAll<HTMLElement>(
          'button, input, select, [href], [tabindex]:not([tabindex="-1"])',
        );
        if (f.length === 0) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      openerRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ("");
      setLive(null);
      setAdding(false);
    }
  }, [open]);

  // A non-empty query always hits the server search (so a roster larger than
  // the cached page is still findable); an empty query browses the cache.
  useEffect(() => {
    if (!open || !trimmed) {
      setLive(null);
      return;
    }
    let cancelled = false;
    api
      .candidates(trimmed)
      .then(r => !cancelled && setLive(r.candidates))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, trimmed]);

  const results = useMemo(() => {
    const needle = trimmed.toLowerCase();
    const pool = needle ? (live ?? candidates) : candidates;
    const filtered = needle
      ? pool.filter(c => c.username.toLowerCase().includes(needle))
      : pool;
    return [...filtered].sort((a, b) => {
      const ra = a.region === region;
      const rb = b.region === region;
      if (ra !== rb) return ra ? -1 : 1;
      return a.username.localeCompare(b.username);
    });
  }, [candidates, live, trimmed, region]);

  if (!open) return null;

  return (
    <div
      className="backdrop-in fixed inset-0 z-50 flex items-start justify-center bg-bg/70 px-4 pt-[12vh] backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Add players"
        className="dialog-in chamfer w-full max-w-md overflow-hidden border border-border-strong bg-overlay shadow-2xl shadow-black/50"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
          <SearchGlyph />
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search pros to watch…"
            className="w-full bg-transparent font-mono text-[13px] text-fg placeholder:text-faint focus:outline-none"
          />
          <kbd className="label-xs shrink-0 rounded border border-border-strong px-1.5 py-0.5">
            esc
          </kbd>
        </div>

        <ul className="max-h-[52vh] overflow-y-auto py-1.5">
          {results.length === 0 ? (
            <li className="px-4 py-7 text-center font-mono text-[11.5px] text-faint">
              {q ? `no verified pros match "${q}"` : "no verified pros yet"}
            </li>
          ) : (
            results.map(c => {
              const on = isWatched(c.epic_id);
              return (
                <li key={c.epic_id}>
                  <button
                    onClick={() => (on ? unwatchPlayer(c.epic_id) : watchPlayer(c.epic_id))}
                    className="press group flex w-full items-center justify-between gap-3 px-4 py-2 text-left transition-colors hover:bg-raised"
                  >
                    <span className="flex min-w-0 items-baseline gap-2">
                      <span
                        className={`truncate text-[13.5px] ${
                          on ? "font-medium text-fg" : "text-dim"
                        }`}
                      >
                        {c.username}
                      </span>
                      <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-faint">
                        {c.region}
                      </span>
                    </span>
                    {on ? (
                      // Watched rows read as a status, but tapping removes — flip
                      // the label to "remove ✕" on hover so that's unmistakable.
                      <span className="relative shrink-0 font-mono text-[10.5px] font-medium uppercase tracking-wider">
                        <span className="text-accent transition-opacity group-hover:opacity-0">
                          watching ✓
                        </span>
                        <span className="absolute inset-0 flex items-center justify-end whitespace-nowrap text-err opacity-0 transition-opacity group-hover:opacity-100">
                          remove ✕
                        </span>
                      </span>
                    ) : (
                      <span className="shrink-0 font-mono text-[10.5px] font-medium uppercase tracking-wider text-faint">
                        + watch
                      </span>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>

        {(admin || merytz) &&
          (adding ? (
            <NewProForm
              defaultName={trimmed}
              defaultRegion={region}
              onCancel={() => setAdding(false)}
              onAdd={async (epicId, name, reg) => {
                const c = await addCandidate(epicId, name, reg);
                watchPlayer(c.epic_id);
                setAdding(false);
                setQ("");
              }}
            />
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="press flex w-full items-center gap-2 border-t border-border px-4 py-2.5 text-left font-mono text-[11px] font-medium uppercase tracking-wider text-faint transition-colors hover:bg-raised hover:text-accent"
            >
              <span className="text-[13px] leading-none">+</span>
              New pro
              <span className="ml-auto rounded border border-border-strong px-1.5 py-0.5 text-[9.5px] text-faint">
                {admin ? "admin" : "merytz"}
              </span>
            </button>
          ))}
      </div>
    </div>
  );
}

const EPIC_ID_RE = /^[0-9a-fA-F]{32}$/;
const REGIONS = ["NAC", "EU", "NAW", "BR", "ASIA", "OCE", "ME"];

/** Gated (admin or merytz): register a brand-new pro (epic id + name +
 *  region) into the allowlist. On success the parent watches them, so they
 *  appear on the dashboard immediately. */
function NewProForm({
  defaultName,
  defaultRegion,
  onAdd,
  onCancel,
}: {
  defaultName: string;
  defaultRegion: string;
  onAdd: (epicId: string, name: string, region: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [epicId, setEpicId] = useState("");
  const [name, setName] = useState(defaultName);
  const [region, setRegion] = useState(
    REGIONS.includes(defaultRegion) ? defaultRegion : REGIONS[0]!,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const id = epicId.trim().toLowerCase();
  const nm = name.trim();
  const valid = EPIC_ID_RE.test(id) && nm.length > 0;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(id, nm, region);
    } catch (e) {
      setError(e instanceof Error ? e.message : "could not add player");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2.5 border-t border-border bg-raised/40 px-4 py-3.5">
      <div className="flex items-center justify-between">
        <span className="label-xs text-dim">New pro</span>
        <button
          onClick={onCancel}
          className="press font-mono text-[10.5px] uppercase tracking-wider text-faint hover:text-fg"
        >
          cancel
        </button>
      </div>

      <input
        autoFocus
        value={epicId}
        onChange={e => setEpicId(e.target.value)}
        onKeyDown={e => e.key === "Enter" && submit()}
        placeholder="epic id — 32 hex chars"
        spellCheck={false}
        className="w-full rounded border border-border bg-bg px-2.5 py-1.5 font-mono text-[12px] text-fg placeholder:text-faint focus:border-border-strong focus:outline-none"
      />
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === "Enter" && submit()}
        placeholder="display name"
        className="w-full rounded border border-border bg-bg px-2.5 py-1.5 font-mono text-[12px] text-fg placeholder:text-faint focus:border-border-strong focus:outline-none"
      />

      <div className="flex items-center gap-2">
        <select
          value={region}
          onChange={e => setRegion(e.target.value)}
          className="rounded border border-border bg-bg px-2 py-1.5 font-mono text-[11px] uppercase tracking-wider text-dim focus:border-border-strong focus:outline-none"
        >
          {REGIONS.map(r => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <button
          onClick={submit}
          disabled={!valid || busy}
          className="press chamfer-sm ml-auto bg-accent/15 px-3.5 py-1.5 font-mono text-[11px] font-medium uppercase tracking-wider text-accent transition-colors hover:bg-accent/25 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? "adding…" : "add + watch"}
        </button>
      </div>

      {error && (
        <p className="font-mono text-[10.5px] leading-relaxed text-warn">{error}</p>
      )}
    </div>
  );
}

function SearchGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4 shrink-0 text-faint"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
